import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  ArrowLeft,
  AlertTriangle,
  Trash2,
  Save,
  Calendar,
  BookOpen,
  Plus,
  Minus,
  ChevronDown,
  ExternalLink,
  Camera,
} from 'lucide-react';
import { useApp } from '@/store';
import { Billing } from '@/data/types';
import { useT, PRESSURE_LABEL_KEYS, type I18nKey } from '@/lib/i18n';
import { addYears } from '@/lib/customer-dates';
import { useConfirm } from '@/components/ConfirmDialog';
import { fmtCurrency } from '@/lib/utils';
import { isPartCompatible } from '@/lib/spare-parts';
import { resolveUploadUrl } from '@/lib/uploads';
import { PhotoUploader } from '@/components/PhotoUploader';
import { ReadOnlyNotice } from '@/components/ReadOnlyNotice';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { ScanButton } from '@/components/BarcodeScanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import type { Extinguisher } from '@/data/types';

const typeDocs: Record<string, Record<string, string>> = {
  'schaum-ab': {
    de: 'Schaum-Feuerlöscher Aufladedruck (AB): Für Brände der Klassen A und B. Jährliche Sichtkontrolle, alle 3 Jahre Hauptwartung, Schaummittel alle 6 Jahre erneuern.',
    en: 'Foam extinguisher boost pressure (AB): For class A & B fires. Annual visual check, full service every 3 years, replace foam every 6 years.',
  },
  'pulver-ab': {
    de: 'Pulver-Feuerlöscher Aufladedruck (AB): Für Klassen A, B, C. Altersgrenze 23 Jahre. Achtung: Pulver kann verklumpen — Rütteltest Pflicht.',
    en: 'Powder extinguisher boost pressure (AB): Class A, B, C. Age limit 23 years. Caution: powder may cake — shake test required.',
  },
  co2: {
    de: 'CO2-Feuerlöscher: Für Klasse B und elektrische Anlagen. Rückstandsfrei. Gewicht vor/nach Service prüfen. Altersgrenze 20 Jahre.',
    en: 'CO2 extinguisher: Class B & electrical. Residue-free. Weigh before/after service. Age limit 20 years.',
  },
  loeschposten: {
    de: 'Löschposten nach SN EN 671-3: Jährliche Kontrolle, alle 5 Jahre Druckprüfung mit 12 Bar. Schlauch visuell prüfen auf Knicke und Risse.',
    en: 'Fire hose per SN EN 671-3: Annual check, 5-year 12 Bar pressure test. Visually inspect hose for kinks and cracks.',
  },
  'fettbrand-ab': {
    de: 'Fettbrand-Feuerlöscher (Klasse F): Für Speiseöle und -fette. Niemals Wasser verwenden. Spezielles Saponifikationsmittel.',
    en: 'Fat-fire extinguisher (class F): For cooking oils and fats. Never use water. Special saponification agent.',
  },
  'lithium-ab': {
    de: 'Lithium-Feuerlöscher: Für Li-Ion-Akkus. Kühlwirkung kritisch. Spezielles Löschmittel. Altersgrenze 18 Jahre.',
    en: 'Lithium extinguisher: For Li-ion batteries. Cooling effect critical. Special agent. Age limit 18 years.',
  },
};

// Coarse agent supercategories (client feedback): fold the many device types
// into the four buckets the technician thinks in. Matches by keyword on the
// device-type id + names so tenant-specific ids still classify.
type SuperCat = 'foam' | 'powder' | 'co2' | 'water'; // enum-ignore
function superCategoryOf(hay: string): SuperCat | null {
  const s = hay.toLowerCase();
  if (/schaum|foam|mousse|schiuma|espuma|schuim|piana/.test(s)) return 'foam';
  if (/pulver|powder|poudre|polvere|polvo|poeder|proszek/.test(s))
    return 'powder';
  if (/co2|kohlendioxid|carbon/.test(s)) return 'co2';
  if (/wasser|water|eau|acqua|agua|woda|löschposten|loeschposten/.test(s))
    return 'water';
  return null;
}

// Display order of the super-category chips + their labels. Which of them are
// actually rendered is decided per manufacturer at runtime (client #10).
const SUPER_CATS: readonly SuperCat[] = ['foam', 'powder', 'co2', 'water'];
const SUPER_CAT_LABEL_KEYS = {
  foam: 'ext.catFoam',
  powder: 'ext.catPowder',
  co2: 'ext.catCo2',
  water: 'ext.catWater',
} as const satisfies Record<SuperCat, I18nKey>;

export function ExtinguisherEdit() {
  const { id, extId } = useParams();
  const nav = useNavigate();
  const t = useT();
  const confirm = useConfirm();
  const lang = useApp(s => s.lang);
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const mfrs = useApp(s => s.manufacturers);
  const types = useApp(s => s.deviceTypes);
  const spareParts = useApp(s => s.spareParts);
  const settings = useApp(s => s.settings);
  const toast = useApp(s => s.toast);
  const upsertExt = useApp(s => s.upsertExtinguisher);
  const deleteExt = useApp(s => s.deleteExtinguisher);
  const existing = customer?.extinguishers.find(e => e.id === extId);
  const rights = useSectionRights();
  const canExt = rights[SectionId.EXTINGUISHERS];

  const [mfrId, setMfrId] = useState(
    mfrs.find(m => m.name === existing?.mfr)?.id ?? ''
  );
  // Devices store manufacturer + model by NAME, and model names are far from
  // unique across the catalogue ("CO2-5" ships under a dozen brands). Resolving
  // the id against every manufacturer's models would match a namesake belonging
  // to someone else, and that id is absent from the selected manufacturer's
  // list — so the model rendered blank on every reopen (client #57). Scope the
  // lookup to the device's own manufacturer.
  const [modelId, setModelId] = useState(
    () =>
      mfrs
        .find(m => m.name === existing?.mfr)
        ?.models.find(m => m.name === existing?.model)?.id ?? ''
  );
  const [mfrOpen, setMfrOpen] = useState(false);
  const [mfrSearch, setMfrSearch] = useState('');
  const [modelOpen, setModelOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  const [modelTypeFilter, setModelTypeFilter] = useState('all');
  const [superCat, setSuperCat] = useState<SuperCat | 'all'>('all');
  // Per-extinguisher doc upload was moved to the manufacturer (client 21 Jul);
  // preserve any legacy per-device value on save but no longer edit it here.
  const docPdf = existing?.documentationPdf ?? '';
  const openUpload = async (stored: string) => {
    try {
      window.open(await resolveUploadUrl(stored), '_blank', 'noopener');
    } catch {
      toast(t('common.error'));
    }
  };
  const [floor, setFloor] = useState(existing?.floor ?? '');
  const [location, setLocation] = useState(existing?.location ?? '');
  const [deviceNumber, setDeviceNumber] = useState(
    existing?.deviceNumber ?? ''
  );
  const [year, setYear] = useState(existing?.year?.toString() ?? '');
  const [lastInsp, setLastInsp] = useState(existing?.lastInspection ?? '');
  const [nextInsp, setNextInsp] = useState(existing?.nextInspection ?? '');
  const [refilled, setRefilled] = useState(existing?.refilled ?? '');
  const [replaced, setReplaced] = useState(existing?.replaced ?? '');
  const [billing, setBilling] = useState(existing?.billing ?? Billing.NONE);
  const [selectedParts, setSelectedParts] = useState<string[]>(
    existing?.spareParts ?? []
  );
  const [photos, setPhotos] = useState<string[]>(existing?.photos ?? []);

  const models = mfrs.find(m => m.id === mfrId)?.models ?? [];
  const selectedMfr = mfrs.find(m => m.id === mfrId);
  const selectedModel = models.find(m => m.id === modelId);
  const type = types.find(x => x.id === selectedModel?.deviceType);
  // Client #51: the maintenance manual describes the device, not the brand, so
  // the model's own PDF wins. The manufacturer-level upload stays as the
  // fallback for the models that haven't got one yet.
  const deviceDocPdf =
    selectedModel?.documentationPdf ?? selectedMfr?.documentationPdf;
  const filteredMfrs = useMemo(
    () =>
      mfrs
        .filter(m => m.name.toLowerCase().includes(mfrSearch.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [mfrs, mfrSearch]
  );
  // Super-category of every device type this manufacturer actually sells.
  // Resolved once per manufacturer so the chip rows and the filter agree on
  // the classification instead of each re-running the keyword match.
  const superCatByType = useMemo(() => {
    const map = new Map<string, SuperCat | null>();
    for (const m of models) {
      if (map.has(m.deviceType)) continue;
      const dt = types.find(x => x.id === m.deviceType);
      const hay = `${m.deviceType} ${dt?.nameDe ?? ''} ${dt?.nameEn ?? ''}`;
      map.set(m.deviceType, superCategoryOf(hay));
    }
    return map;
  }, [models, types]);

  // Client #10: the chip rows used to be hardcoded (all five super-categories)
  // plus every DeviceType in the store, so the picker offered filters that
  // matched nothing for this manufacturer, and kept showing CO2/Powder after
  // Foam had been picked. Derive both rows from the models on offer and nest
  // the device-type row inside the chosen super-category, hiding anything that
  // would return zero models. The free-text search is deliberately left out of
  // this: chips vanishing on every keystroke made the row jump under the
  // technician's finger.
  const availableSuperCats = useMemo(() => {
    const present = new Set(superCatByType.values());
    return SUPER_CATS.filter(c => present.has(c));
  }, [superCatByType]);

  // The legacy import left several DeviceType rows sharing a label (two
  // separate "Schaum Dauerdruck" ids, for instance). Rendered straight, that
  // draws the same chip twice and each copy filters away the other's models.
  // Group the chips by label and keep every id behind the one the user clicks.
  const availableTypes = useMemo(() => {
    const ids = new Set(
      models
        .filter(
          m =>
            superCat === 'all' || superCatByType.get(m.deviceType) === superCat
        )
        .map(m => m.deviceType)
    );
    const byLabel = new Map<
      string,
      { id: string; nameDe: string; ids: string[] }
    >();
    for (const dt of types) {
      if (!ids.has(dt.id)) continue;
      // Key on the label the chip actually renders, so two rows that look
      // identical to the technician collapse into one chip.
      const key = dt.nameDe.trim().toLowerCase();
      const seen = byLabel.get(key);
      if (seen) seen.ids.push(dt.id);
      else byLabel.set(key, { id: dt.id, nameDe: dt.nameDe, ids: [dt.id] });
    }
    return [...byLabel.values()];
  }, [models, types, superCat, superCatByType]);

  // Chip id -> every DeviceType id it stands for.
  const typeGroupIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const g of availableTypes) map.set(g.id, new Set(g.ids));
    return map;
  }, [availableTypes]);

  // Dozens of models per manufacturer (client feedback) — searchable and
  // filterable by device type, alphabetical.
  const filteredModels = useMemo(() => {
    const q = modelSearch.toLowerCase();
    return models
      .filter(m => {
        if (modelTypeFilter !== 'all') {
          const group = typeGroupIds.get(modelTypeFilter);
          if (
            group ? !group.has(m.deviceType) : m.deviceType !== modelTypeFilter
          )
            return false;
        }
        if (superCat !== 'all' && superCatByType.get(m.deviceType) !== superCat)
          return false;
        return !q || `${m.name} ${m.content}`.toLowerCase().includes(q);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    models,
    modelSearch,
    modelTypeFilter,
    superCat,
    superCatByType,
    typeGroupIds,
  ]);

  const ageWarning = useMemo(() => {
    if (!year || !type) return null;
    const age = new Date().getFullYear() - parseInt(year, 10);
    if (age > type.ageLimit)
      return { age, limit: type.ageLimit, cls: type.pressureClass };
    return null;
  }, [year, type]);

  const yearInvalid = useMemo(() => {
    if (!year) return false;
    const y = parseInt(year, 10);
    if (isNaN(y)) return true;
    const now = new Date().getFullYear();
    return y < now - 50 || y > now;
  }, [year]);

  const setToday = (setter: (v: string) => void) =>
    setter(new Date().toISOString().slice(0, 10));
  // Client #50: booking a device as NEW means it was swapped out, so stamp the
  // replacement date instead of making the technician type it. Only when empty
  // — a date already on the device (or just corrected by hand) is the truth and
  // must survive toggling the billing class back and forth.
  const changeBilling = (v: Billing) => {
    setBilling(v);
    if (v === Billing.NEW && !replaced) setToday(setReplaced);
  };
  // Some customers are serviced every 2 or 3 years rather than on the tenant's
  // default interval, so the technician gets one button per cycle (client #78).
  const computeNext = (years: number) => {
    if (lastInsp) setNextInsp(addYears(lastInsp, years));
  };

  if (!customer) return <div>{t('common.notFound')}</div>;
  // Section rights (#95): no new-device form without Erstellen, and an
  // existing device opens read-only without Bearbeiten.
  if (!existing && !canExt.create) {
    return <Navigate to={`/customer/${customer.id}/extinguishers`} replace />;
  }
  const readOnly = !!existing && !canExt.edit;

  const aggregatedParts = selectedParts.reduce<Record<string, number>>(
    (acc, a) => {
      acc[a] = (acc[a] ?? 0) + 1;
      return acc;
    },
    {}
  );

  const save = async () => {
    if (!selectedMfr || !selectedModel || !type) {
      toast('Select manufacturer and model');
      return;
    }
    if (yearInvalid) {
      toast('Invalid manufacturing year');
      return;
    }
    const dup =
      !!deviceNumber &&
      [...customer.extinguishers, ...customer.hoses].some(
        d => d.id !== existing?.id && d.deviceNumber === deviceNumber
      );
    if (dup && !(await confirm(t('device.dupWarning')))) return;
    const ext: Extinguisher = {
      id: existing?.id ?? `E-${Date.now()}`,
      floor,
      location,
      mfr: selectedMfr.name,
      model: selectedModel.name,
      content: selectedModel.content,
      type: type.id,
      deviceNumber,
      year: year ? parseInt(year, 10) : undefined,
      lastInspection: lastInsp || undefined,
      nextInspection: nextInsp || undefined,
      refilled: refilled || undefined,
      replaced: replaced || undefined,
      billing,
      spareParts: selectedParts,
      photos: photos.length ? photos : undefined,
      notes: existing?.notes,
      section: existing?.section,
      documentationPdf: docPdf || undefined,
    };
    upsertExt(customer.id, ext);
    toast(t('common.save'));
    nav(`/customer/${customer.id}/extinguishers`);
  };

  const handleDelete = async () => {
    if (!existing) return;
    if (
      !(await confirm(
        `${t('common.delete')} ${t('parts.extinguisher')} ${existing.deviceNumber ?? existing.id}?`
      ))
    )
      return;
    deleteExt(customer.id, existing.id);
    toast(t('common.delete'));
    nav(`/customer/${customer.id}/extinguishers`);
  };

  const docs = type ? typeDocs[type.id] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-tour="extinguisher-edit"
    >
      <div>
        <Link
          to={`/customer/${customer.id}/extinguishers`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        {existing && rights[SectionId.MAINTENANCE].read && (
          <Link
            to={`/customer/${customer.id}/device/${existing.id}/history`}
            className="ml-4 text-sm text-primary"
          >
            {t('customer.inspections')}
          </Link>
        )}
        <h1 className="text-2xl font-bold tracking-tight mt-2">
          {existing
            ? readOnly
              ? t('ext.title')
              : `${t('common.edit')} ${t('ext.title')}`
            : t('ext.addExtinguisher')}
        </h1>
        {existing && (
          <p className="text-sm text-muted-foreground font-mono">
            {existing.id}
          </p>
        )}
      </div>

      {readOnly && <ReadOnlyNotice />}

      {ageWarning && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <span className="font-semibold text-amber-700 dark:text-amber-300">
                {ageWarning.age}y
              </span>{' '}
              <span>
                {t('ext.ageWarning')}: {ageWarning.limit}y ({ageWarning.cls})
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* A disabled fieldset locks every control of the form at once. */}
      <fieldset
        disabled={readOnly}
        className="grid min-w-0 gap-4 sm:grid-cols-2"
      >
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('ext.type')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>{t('ext.manufacturer')}</Label>
                <div className="relative" data-focus="mfr-search">
                  <button
                    data-testid="ext-mfr-trigger"
                    type="button"
                    onClick={() => setMfrOpen(o => !o)}
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent"
                  >
                    <span
                      className={selectedMfr ? '' : 'text-muted-foreground'}
                    >
                      {selectedMfr?.name ?? t('ext.manufacturer')}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </button>
                  {mfrOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md">
                      <Input
                        placeholder={t('ext.mfrSearch')}
                        value={mfrSearch}
                        onChange={e => setMfrSearch(e.target.value)}
                        className="rounded-b-none border-0 border-b focus-visible:ring-0"
                        autoFocus
                      />
                      <div className="max-h-48 sm:max-h-64 overflow-auto">
                        {filteredMfrs.map(m => (
                          <button
                            key={m.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                            onClick={() => {
                              setMfrId(m.id);
                              setModelId('');
                              setMfrSearch('');
                              setMfrOpen(false);
                              // The chips describe the previous manufacturer's
                              // catalogue; carrying them over would filter the
                              // new list down to nothing (#10).
                              setSuperCat('all');
                              setModelTypeFilter('all');
                            }}
                          >
                            {m.name}
                          </button>
                        ))}
                        {filteredMfrs.length === 0 && (
                          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                            {t('common.notFound')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label>{t('ext.model')}</Label>
                <div className="relative">
                  <button
                    data-testid="ext-model-trigger"
                    type="button"
                    disabled={!mfrId}
                    onClick={() => setModelOpen(o => !o)}
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span
                      className={selectedModel ? '' : 'text-muted-foreground'}
                    >
                      {selectedModel
                        ? `${selectedModel.name} — ${selectedModel.content}`
                        : t('ext.model')}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </button>
                  {modelOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md">
                      <Input
                        placeholder={t('ext.modelSearch')}
                        value={modelSearch}
                        onChange={e => setModelSearch(e.target.value)}
                        className="rounded-b-none border-0 border-b focus-visible:ring-0"
                        autoFocus
                      />
                      {/* A single remaining chip filters nothing away, so the
                          whole row is dropped rather than shown inert (#10). */}
                      {availableSuperCats.length > 1 && (
                        <div
                          className="flex flex-wrap gap-1 border-b p-2"
                          data-testid="ext-cat-row"
                        >
                          <button
                            type="button"
                            data-testid="ext-cat-all"
                            onClick={() => {
                              setSuperCat('all');
                              setModelTypeFilter('all');
                            }}
                            className={`rounded-full border px-2 py-1 text-xs font-medium ${
                              superCat === 'all'
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'hover:bg-accent'
                            }`}
                          >
                            {t('common.all')}
                          </button>
                          {availableSuperCats.map(key => (
                            <button
                              key={key}
                              type="button"
                              data-testid={`ext-cat-${key}`}
                              onClick={() => {
                                setSuperCat(key);
                                setModelTypeFilter('all');
                              }}
                              className={`rounded-full border px-2 py-1 text-xs font-medium ${
                                superCat === key
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'hover:bg-accent'
                              }`}
                            >
                              {t(SUPER_CAT_LABEL_KEYS[key])}
                            </button>
                          ))}
                        </div>
                      )}
                      {availableTypes.length > 1 && (
                        <div
                          className="flex flex-wrap gap-1 border-b p-2"
                          data-testid="ext-type-row"
                        >
                          {/* Clearing the device type keeps the super-category
                              selected — the rows are nested, not siblings. */}
                          <button
                            type="button"
                            data-testid="ext-type-all"
                            onClick={() => setModelTypeFilter('all')}
                            className={`rounded-full border px-2 py-1 text-xs ${
                              modelTypeFilter === 'all'
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'hover:bg-accent'
                            }`}
                          >
                            {t('common.all')}
                          </button>
                          {availableTypes.map(dt => (
                            <button
                              key={dt.id}
                              type="button"
                              data-testid={`ext-type-${dt.id}`}
                              onClick={() => setModelTypeFilter(dt.id)}
                              className={`rounded-full border px-2 py-1 text-xs ${
                                modelTypeFilter === dt.id
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'hover:bg-accent'
                              }`}
                            >
                              {dt.nameDe}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="max-h-48 sm:max-h-64 overflow-auto">
                        {filteredModels.map(m => (
                          <button
                            key={m.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                            onClick={() => {
                              setModelId(m.id);
                              setModelSearch('');
                              setModelOpen(false);
                            }}
                          >
                            {m.name} — {m.content}
                          </button>
                        ))}
                        {filteredModels.length === 0 && (
                          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                            {t('common.notFound')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {selectedModel && type && (
                <div className="rounded-md bg-primary/5 p-3 text-xs space-y-1">
                  <div className="font-semibold">{selectedModel.name}</div>
                  <div>
                    {t('ext.content')}: {selectedModel.content}
                  </div>
                  <div>
                    {t('ext.type')}: {type.nameDe} ({type.nameEn})
                  </div>
                  <div>
                    {t('ext.pressure')}:{' '}
                    <Badge variant="outline">
                      {t(PRESSURE_LABEL_KEYS[type.pressureClass])}
                    </Badge>
                  </div>
                  <div>
                    {t('ext.ageLimit')}: {type.ageLimit}y
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {docs && (
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  {t('ext.documentation')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs leading-relaxed text-muted-foreground">
                {docs[lang] ?? docs.en}
              </CardContent>
            </Card>
          )}

          <Card data-focus="photo-docs">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Camera className="h-4 w-4 text-primary" />
                {t('photos.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PhotoUploader value={photos} onChange={setPhotos} />
            </CardContent>
          </Card>

          {deviceDocPdf && (
            <Card data-focus="doc-view">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  {t('ext.documentation')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* A link, not a button, so it still opens when the form
                    is read-only (a disabled fieldset disables buttons). */}
                <Button variant="outline" size="sm" asChild>
                  <a
                    href="#"
                    role="button"
                    data-testid="ext-view-doc"
                    onClick={ev => {
                      ev.preventDefault();
                      openUpload(deviceDocPdf);
                    }}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t('ext.viewDoc')}
                  </a>
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('ext.placement')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('ext.floor')}</Label>
                <Input
                  data-testid="ext-floor"
                  value={floor}
                  onChange={e => setFloor(e.target.value)}
                />
              </div>
              <div>
                <Label>{t('ext.location')}</Label>
                <Input
                  data-testid="ext-location"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                />
              </div>
              <div>
                <Label>{t('ext.idNumber')}</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="ext-device-number"
                    value={deviceNumber}
                    onChange={e => setDeviceNumber(e.target.value)}
                  />
                  <ScanButton onScan={setDeviceNumber} />
                </div>
              </div>
              <div>
                <Label>{t('ext.year')}</Label>
                <Input
                  data-testid="ext-year"
                  type="number"
                  value={year}
                  onChange={e => setYear(e.target.value)}
                  className={yearInvalid ? 'border-red-500' : ''}
                />
                {yearInvalid && (
                  <div className="mt-1 text-xs text-red-500">
                    {t('common.invalidYear')}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                {t('ext.inspectionDates')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>{t('ext.lastInspection')}</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="ext-last-inspection"
                    type="date"
                    value={lastInsp}
                    onChange={e => setLastInsp(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToday(setLastInsp)}
                  >
                    <Calendar className="h-3 w-3" />
                    {t('common.today')}
                  </Button>
                </div>
              </div>
              <div>
                <Label>{t('ext.nextInspection')}</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="ext-next-inspection"
                    type="date"
                    value={nextInsp}
                    onChange={e => setNextInsp(e.target.value)}
                  />
                  {[1, 2, 3].map(y => (
                    <Button
                      key={y}
                      variant="outline"
                      size="sm"
                      data-testid={`ext-next-plus-${y}y`}
                      onClick={() => computeNext(y)}
                    >
                      +{y}y
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t('ext.refilled')}</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={refilled}
                    onChange={e => setRefilled(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToday(setRefilled)}
                  >
                    <Calendar className="h-3 w-3" />
                    {t('common.today')}
                  </Button>
                </div>
              </div>
              {/* Sits with the other inspection dates because the technician
                  fills the whole block in one pass; auto-stamped by the NEW
                  billing class but always overridable (client #50). */}
              <div>
                <Label htmlFor="ext-replaced">{t('ext.replaced')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="ext-replaced"
                    data-testid="ext-replaced"
                    type="date"
                    value={replaced}
                    onChange={e => setReplaced(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToday(setReplaced)}
                  >
                    <Calendar className="h-3 w-3" />
                    {t('common.today')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('ext.billing')}</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={billing}
                onValueChange={v => changeBilling(v as typeof billing)}
              >
                {[
                  { v: Billing.NONE, l: t('ext.billingNone') },
                  { v: Billing.NEW, l: t('ext.billingNew') },
                  { v: Billing.MAINTENANCE, l: t('ext.billingMaintenance') },
                  { v: Billing.DISPOSED, l: t('ext.billingDisposed') },
                ].map(o => (
                  <div key={o.v} className="flex items-center gap-2">
                    <RadioGroupItem value={o.v} id={`b-${o.v}`} />
                    <Label htmlFor={`b-${o.v}`} className="text-sm">
                      {o.l}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {billing === Billing.MAINTENANCE && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('ext.spareParts')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1 max-h-48 overflow-auto border rounded-md p-2">
                    {spareParts
                      .filter(
                        p =>
                          p.type === 'extinguisher' &&
                          isPartCompatible(
                            p,
                            {
                              mfr: selectedMfr?.name,
                              model: selectedModel?.name,
                            },
                            mfrs
                          )
                      )
                      .map(p => (
                        <button
                          key={p.articleNr}
                          onClick={() =>
                            setSelectedParts([...selectedParts, p.articleNr])
                          }
                          className="w-full text-left text-xs px-2 py-1 rounded hover:bg-accent flex justify-between"
                        >
                          <span>
                            <span className="font-mono text-muted-foreground">
                              {p.articleNr}
                            </span>{' '}
                            {p.name}
                          </span>
                          <span className="text-muted-foreground">
                            {fmtCurrency(p.price, settings.currency)}
                          </span>
                        </button>
                      ))}
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground mb-1">
                      {Object.keys(aggregatedParts).length} /{' '}
                      {selectedParts.length}
                    </div>
                    {Object.entries(aggregatedParts).map(([a, qty]) => {
                      const part = spareParts.find(p => p.articleNr === a);
                      return (
                        <div
                          key={a}
                          className="flex justify-between items-center text-xs px-2 py-2 sm:py-1 rounded bg-primary/5 gap-2"
                        >
                          <span className="flex-1 truncate">
                            {part?.name ?? a}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 sm:h-5 sm:w-5"
                              onClick={() => {
                                const idx = selectedParts.indexOf(a);
                                if (idx >= 0) {
                                  const copy = [...selectedParts];
                                  copy.splice(idx, 1);
                                  setSelectedParts(copy);
                                }
                              }}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="font-mono w-5 text-center">
                              {qty}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 sm:h-5 sm:w-5"
                              onClick={() =>
                                setSelectedParts([...selectedParts, a])
                              }
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </fieldset>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => nav(`/customer/${customer.id}/extinguishers`)}
        >
          {readOnly ? t('common.back') : t('common.cancel')}
        </Button>
        {existing && canExt.delete && (
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            {t('common.delete')}
          </Button>
        )}
        {!readOnly && (
          <Button data-testid="ext-save" onClick={save}>
            <Save className="h-4 w-4" />
            {t('common.save')}
          </Button>
        )}
      </div>
    </motion.div>
  );
}
